/**
 * GameStore.ts — observable mirror of game state.
 *
 * The store holds the client's authoritative-mirror view: balance, the selected
 * picture instance, per-color buy/progress state, the running picture-bonus
 * meter, autoplay status, fairness info. Scenes/UI subscribe to slices.
 */
import { Emitter } from '../util/emitter';
import type {
  CellDef,
  MathConfig,
  PaletteEntry,
  PictureSummary,
} from '../net/contract';

export interface ColorState {
  colorId: number;
  number: number;
  hex: string;
  cellCount: number;
  bought: boolean;
  betId?: string;
  stake?: number;
  revealed: number; // cells revealed
  completed: boolean;
  earned: number; // running coins from this color (drip + stardust)
}

export interface PictureRuntime {
  pictureInstanceId: string;
  pictureId: string;
  title: string;
  sizeName: string;
  view: number;
  K: number;
  palette: PaletteEntry[];
  cells: CellDef[];
  colors: Map<number, ColorState>;
  totalStake: number;
  totalEarned: number;
  cellsTotal: number;
  cellsRevealed: number;
  pictureBonusAwarded?: number;
  projectedTeaser: number;
}

export type StoreEvents = {
  balance: number;
  pictureLoaded: PictureRuntime;
  colorChanged: ColorState;
  progress: { revealed: number; total: number };
  fomo: { colorsRemaining: number; teaser: number };
  pictureComplete: { amount: number; mult: number; tier: string };
  autoplay: { running: boolean; runId: string | null };
  sessionReady: void;
  stakeChanged: number;
  /** Cumulative session counters (wagered, paid, net = paid - wagered). Drives always-on Net P/L. */
  sessionPL: { wagered: number; paid: number; net: number; sessionStart: number };
};

export class GameStore {
  readonly events = new Emitter<StoreEvents>();

  // session
  sessionId = '';
  serverSeedHash = '';
  clientSeed = '';
  nonce = 0;
  mathVersion = '';
  currency = 'USD';
  mathConfig: MathConfig | null = null;

  private _balance = 0;
  selectedStake = 1;

  gallery: PictureSummary[] = [];
  picture: PictureRuntime | null = null;

  autoplayRunning = false;
  autoplayRunId: string | null = null;

  // session P/L (always-on net display + reality-check evidence)
  cumWagered = 0;
  cumPaid = 0;
  sessionStartMs: number = Date.now();

  // Timestamp of the most recent picture completion. Lets the gallery highlight the next
  // suggested card as a "paint next" CTA after returning from the cinematic.
  lastCompletedAt = 0;

  get balance(): number {
    return this._balance;
  }
  setBalance(v: number): void {
    if (v === this._balance) return;
    this._balance = +v.toFixed(2);
    this.events.emit('balance', this._balance);
  }

  setStake(v: number): void {
    if (this.selectedStake === v) return;
    this.selectedStake = v;
    this.events.emit('stakeChanged', v);
  }

  loadPicture(rt: PictureRuntime): void {
    this.picture = rt;
    this.events.emit('pictureLoaded', rt);
    this.events.emit('progress', { revealed: rt.cellsRevealed, total: rt.cellsTotal });
  }

  color(colorId: number): ColorState | undefined {
    return this.picture?.colors.get(colorId);
  }

  markBought(colorId: number, betId: string, stake: number): void {
    const c = this.picture?.color(colorId);
    if (!c) return;
    c.bought = true;
    c.betId = betId;
    c.stake = stake;
    this.picture!.totalStake = +(this.picture!.totalStake + stake).toFixed(2);
    this.cumWagered = +(this.cumWagered + stake).toFixed(2);
    this.events.emit('colorChanged', c);
    this.emitPL();
  }

  private emitPL(): void {
    this.events.emit('sessionPL', {
      wagered: this.cumWagered, paid: this.cumPaid,
      net: +(this.cumPaid - this.cumWagered).toFixed(2),
      sessionStart: this.sessionStartMs,
    });
  }

  recordCellReveal(colorId: number, sub: number): void {
    const pic = this.picture;
    if (!pic) return;
    const c = pic.colors.get(colorId);
    if (!c) return;
    c.revealed++;
    c.earned = +(c.earned + sub).toFixed(2);
    pic.cellsRevealed++;
    pic.totalEarned = +(pic.totalEarned + sub).toFixed(2);
    if (sub > 0) {
      this.cumPaid = +(this.cumPaid + sub).toFixed(2);
      this.emitPL();
    }
    this.events.emit('colorChanged', c);
    this.events.emit('progress', {
      revealed: pic.cellsRevealed,
      total: pic.cellsTotal,
    });
  }

  recordColorComplete(colorId: number, bonus: number): void {
    const pic = this.picture;
    const c = pic?.colors.get(colorId);
    if (!pic || !c) return;
    c.completed = true;
    c.earned = +(c.earned + bonus).toFixed(2);
    pic.totalEarned = +(pic.totalEarned + bonus).toFixed(2);
    if (bonus > 0) {
      this.cumPaid = +(this.cumPaid + bonus).toFixed(2);
      this.emitPL();
    }
    this.events.emit('colorChanged', c);
  }

  setFomo(colorsRemaining: number, teaser: number): void {
    if (this.picture) this.picture.projectedTeaser = teaser;
    this.events.emit('fomo', { colorsRemaining, teaser });
  }

  recordPictureComplete(amount: number, mult: number, tier: string): void {
    if (this.picture) this.picture.pictureBonusAwarded = amount;
    if (amount > 0) {
      this.cumPaid = +(this.cumPaid + amount).toFixed(2);
      this.emitPL();
    }
    this.lastCompletedAt = Date.now();
    this.events.emit('pictureComplete', { amount, mult, tier });
  }

  setAutoplay(running: boolean, runId: string | null): void {
    this.autoplayRunning = running;
    this.autoplayRunId = runId;
    this.events.emit('autoplay', { running, runId });
  }

  /** colors completed / total. */
  completion(): { done: number; total: number } {
    const pic = this.picture;
    if (!pic) return { done: 0, total: 0 };
    let done = 0;
    pic.colors.forEach((c) => c.completed && done++);
    return { done, total: pic.K };
  }
}

// helper added to PictureRuntime via prototype-like access
declare module './GameStore' {
  interface PictureRuntime {
    color(colorId: number): ColorState | undefined;
  }
}

// attach convenience method on created runtimes (see factory below)
export function makePictureRuntime(
  base: Omit<PictureRuntime, 'color' | 'colors'> & { colors: Map<number, ColorState> },
): PictureRuntime {
  const rt = base as PictureRuntime;
  rt.color = (id: number) => rt.colors.get(id);
  return rt;
}
